import { FC, memo, useCallback, useMemo, lazy, Suspense } from 'react';
import Modal from '@/components/Modal/Modal';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import TabHeader from '@/components/TabHeader/TabHeader';
// import SummaryTab from '@/features/rewards/Tabs/SummaryTab/SummaryTab';
// import TierBenefitsTab from '@/features/rewards/Tabs/TierBenefitsTab/TierBenefitsTab';
// import AboutTab from '@/features/rewards/Tabs/AboutTab/AboutTab';
// import RedeemTab from '@/features/rewards/Tabs/RedeemTab/RedeemTab';
import useIsMobileView from '@/hooks/useIsMobileView';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';

const SummaryTab = lazy(() => import('@/features/rewards/Tabs/SummaryTab/SummaryTab'));
const TierBenefitsTab = lazy(() => import('@/features/rewards/Tabs/TierBenefitsTab/TierBenefitsTab'));
const AboutTab = lazy(() => import('@/features/rewards/Tabs/AboutTab/AboutTab'));
const RedeemTab = lazy(() => import('@/features/rewards/Tabs/RedeemTab/RedeemTab'));

const rewardsTabs: string[] = ['SUMMARY', 'TIER BENEFITS', 'ABOUT PHNM', 'REDEEM'];

interface RewardsModalProps {
  open: boolean;
  setClose: () => void;
  defaultTab?: number;
}
const RewardsModal: FC<RewardsModalProps> = ({ open, setClose, defaultTab = 0 }) => {
  const isMobileView = useIsMobileView();
  const classes = useMemo(() => (isMobileView ? '!px-0' : ''), [isMobileView]);
  const titleClasses = useMemo(() => (isMobileView ? 'px-4' : ''), [isMobileView]);
  const handleOnClose = useCallback(() => {
    setClose();
  }, [setClose]);

  const MemoizedTabHeader = memo(TabHeader);
  const memoizedModalTitle = useMemo(
    () => <Modal.Title onClose={handleOnClose} title="Rewards Manager" className={titleClasses} />,
    [handleOnClose, titleClasses],
  );

  return (
    <Modal
      open={open}
      setClose={handleOnClose}
      className={
        isMobileView
          ? 'h-full w-full'
          : 'max-h-[min(728px,calc(100vh-50px))] min-h-[min(728px,calc(100vh-50px))] min-w-[min(950px,calc(100vw-50px))] max-w-[min(950px,calc(100vw-50px))]'
      }
      bodyClassName="overflow-y-auto scrollbar-phenom"
      contentClassName={classes}
      titleComponent={memoizedModalTitle}
    >
      <TabGroup defaultIndex={defaultTab} className="flex h-full flex-col">
        <div className="mb-6 shrink-0 overflow-x-auto scrollbar-thin">
          <TabList className="flex w-full min-w-max whitespace-nowrap px-4 md:px-0">
            {rewardsTabs.map((tab) => (
              <Tab className="w-full focus:outline-none" key={tab}>
                {({ selected }) => <MemoizedTabHeader tabLabel={tab} selected={selected} />}
              </Tab>
            ))}
          </TabList>
        </div>
        <TabPanels className="flex flex-auto">
          <TabPanel className="flex flex-auto">
            <Suspense fallback={<LoadingSpinner />}>
              <SummaryTab />
            </Suspense>
          </TabPanel>
          <TabPanel className="flex flex-auto">
            <Suspense fallback={<LoadingSpinner />}>
              <TierBenefitsTab />
            </Suspense>
          </TabPanel>
          <TabPanel className="flex flex-auto">
            <Suspense fallback={<LoadingSpinner />}>
              <AboutTab />
            </Suspense>
          </TabPanel>
          <TabPanel className="flex flex-auto">
            <Suspense fallback={<LoadingSpinner />}>
              <RedeemTab />
            </Suspense>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Modal>
  );
};

export default RewardsModal;
